import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "../lib/api";
import AppLayout from "../components/app/AppLayout";
import { Sparkles, TrendingUp, Clock, DollarSign, Wrench } from "lucide-react";

export default function MachineRoiPage() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const analyze = async () => {
    setLoading(true);
    try { const { data } = await api.get("/machine-roi"); setData(data); }
    catch (e) { console.error("MachineRoi fetch failed:", e); }
    setLoading(false);
  };
  useEffect(() => { analyze(); }, []);

  return (
    <AppLayout>
      <div className="px-8 py-8 max-w-6xl mx-auto" data-testid="machine-roi-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="label-tech mb-2">{t("machineRoi.label")}</div>
            <h1 className="font-display font-bold text-3xl text-slate-100 flex items-center gap-3"><Wrench size={26} className="text-blue-400"/> {t("machineRoi.title")}</h1>
            <p className="text-slate-400 text-sm mt-2 max-w-3xl">{t("machineRoi.intro")}</p>
          </div>
          <button onClick={analyze} disabled={loading} className="btn-primary px-5 py-3 rounded-md flex items-center gap-2 disabled:opacity-50" data-testid="reanalyze-btn">
            <Sparkles size={16} className={loading?"animate-pulse":""}/> {loading?t("machineRoi.analyzing"):t("machineRoi.reanalyze")}
          </button>
        </div>

        {data && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <Stat label={t("machineRoi.outsourcedCount")} value={data.lost_or_outsourced_quote_count}/>
              <Stat label={t("machineRoi.ownedCategories")} value={(data.owned_categories||[]).length}/>
            </div>

            {(data.recommendations||[]).length === 0 ? (
              <div className="tech-card p-12 text-center" data-testid="no-recommendations">
                <Wrench size={28} className="mx-auto text-slate-500 mb-3"/>
                <div className="font-display font-medium text-slate-100">{t("machineRoi.emptyTitle")}</div>
                <div className="text-slate-500 text-sm mt-1">{t("machineRoi.emptyHint")}</div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {data.recommendations.map((r, i) => (
                  <div key={r.model_suggestion || `reco-${i}`} className="tech-card p-6" data-testid={`reco-${i}`}>
                    <div className="label-tech mb-2">{t("machineRoi.recommendation")} #{i+1}</div>
                    <div className="font-display font-bold text-xl text-slate-100">{r.model_suggestion}</div>
                    <div className="text-sm text-slate-500 mt-1 capitalize">{r.machine_category}</div>
                    <p className="text-sm text-slate-300 mt-3 leading-relaxed">{r.rationale}</p>
                    <div className="mt-5 space-y-2 pt-4 border-t border-slate-800">
                      <Kv icon={<DollarSign size={12}/>} label={t("machineRoi.investment")} value={`€ ${r.estimated_investment_eur}`}/>
                      <Kv icon={<TrendingUp size={12}/>} label={t("machineRoi.jobsPerYear")} value={`${r.potential_jobs_per_year}`}/>
                      <Kv icon={<Clock size={12}/>} label={t("machineRoi.payback")} value={`${r.payback_months} ${t("machineRoi.months")}`}/>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </AppLayout>
  );
}

function Stat({ label, value }) {
  return <div className="tech-card p-4"><div className="label-tech mb-2">{label}</div><div className="font-mono-data text-3xl text-blue-400">{value}</div></div>;
}
function Kv({ icon, label, value }) {
  return <div className="flex items-center justify-between text-sm"><span className="text-slate-500 flex items-center gap-1">{icon} {label}</span><span className="text-slate-100 font-mono-data">{value}</span></div>;
}
